import { useEffect } from 'react';

// Shared client-side counterpart to the per-route <head> tags that
// scripts/prerender-main-site.mjs bakes into the static HTML at build time
// (see prerenderRoutes.ts's `extraHead`). Needed so `npm run dev` — which
// never runs the prerender step — still shows the right
// title/description/canonical/hreflang/robots/JSON-LD locally, and as a
// second layer of correctness after hydration.
//
// index.html already ships a site-wide default <meta name="description"> and
// <link rel="canonical">, so those two must be found-and-reused by selector
// (not just by our own data-seo-head marker) or this ends up with two
// competing canonical/description tags — one from index.html, one from here.
// hreflang tags have no site-wide default, so those are safe to create fresh.
//
// Originally scoped to the SVS guide pages only (components/svs-guide/); now
// the one reusable SEO-head mechanism for the whole site (Home + legal pages
// use it too) as part of the DE/EN URL migration, since nothing about the
// implementation was actually guide-specific.
interface HreflangEntry {
  lang: string;
  href: string;
}

interface SeoHeadOptions {
  title: string;
  description: string;
  canonical: string;
  indexable: boolean;
  htmlLang: 'en' | 'de';
  hreflang: HreflangEntry[];
  jsonLd?: object[];
}

function upsertMeta(name: string, content: string) {
  let el = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertHreflang(lang: string, href: string) {
  let el = document.head.querySelector(`link[rel="alternate"][hreflang="${lang}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', lang);
    el.setAttribute('data-seo-head', 'hreflang');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeoHead(opts: SeoHeadOptions) {
  useEffect(() => {
    document.title = opts.title;
    document.documentElement.setAttribute('lang', opts.htmlLang);

    upsertMeta('description', opts.description);
    upsertMeta('robots', opts.indexable ? 'index, follow' : 'noindex, nofollow');
    upsertCanonical(opts.canonical);
    opts.hreflang.forEach((entry) => upsertHreflang(entry.lang, entry.href));

    document.head.querySelectorAll('script[data-seo-head-jsonld]').forEach((el) => el.remove());
    (opts.jsonLd ?? []).forEach((data, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-head-jsonld', String(i));
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.title, opts.description, opts.canonical, opts.indexable, opts.htmlLang]);
}
